import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import {
  SupplierData,
  SupplierCategory,
} from "../Helpers/Interfaces/SupplierType";
import {
  ProductAndSupplierMapModel,
  Convert,
} from "../Helpers/Interfaces/ProductAndSupplierMapType";
import {
  InventoryLogsResponse,
  RecordsList,
} from "../Helpers/Interfaces/InventoryType";

export interface SupplierListResponse {
  data: SupplierData[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export interface SupplierListParams {
  page: number;
  pageSize: number;
}

export interface UpdateSupplierRequest {
  _id?: string;
  data: SupplierData;
}

export interface MembershipData {
  supplierLevel: string;
  value: number;
  description: string;
  isActive: boolean;
  _id?: string;
  icon?: string;
}

export interface SupplierDetailResponse {
  status: string;
  data: SupplierData;
}

export const SupplierService = createApi({
  reducerPath: "SupplierService",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: [
    "Suppliers",
    "Memberships",
    "ProductSuppliersMapping",
    "SupplierTopupSettings",
    "SUPPLIER_PROVIDER",
  ],
  endpoints: (builder) => ({
    getSupplierList: builder.query<SupplierListResponse, SupplierListParams>({
      query: (params) => ({
        url: `${ApiPath.getSupplierList}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "GET",
      }),
    }),

    getAllSupplierList: builder.query<
      SupplierListResponse,
      {
        page: number;
        pageSize: number;
        search?: string;
        filters?: { key: string; value: string }[];
      }
    >({
      query: (params) => ({
        url: `${ApiPath.getAllSupplierList}`,
        method: "POST",
        body: {
          page: params.page,
          pageSize: params.pageSize,
          search: params.search || "",

          filters: params.filters || [],
        },
      }),
      transformResponse: (response: any): SupplierListResponse => {
        // Normalize backend 202 No Records body and other non-standard shapes
        if (response && response.status === "error" && response.statusCode === 202) {
          return {
            data: [],
            totalRecords: 0,
            totalPages: 0,
            currentPage: 1,
          };
        }

        const data = Array.isArray(response?.data) ? response.data : [];
        const totalRecords = typeof response?.totalRecords === "number" ? response.totalRecords : (Array.isArray(data) ? data.length : 0);
        const totalPages = typeof response?.totalPages === "number" ? response.totalPages : 0;
        const currentPage = typeof response?.currentPage === "number" ? response.currentPage : 1;

        return {
          data,
          totalRecords,
          totalPages,
          currentPage,
        };
      },
      providesTags: ["Suppliers"],
    }),

    getSupplierDetails: builder.query<SupplierData, string>({
      query: (id) => ({
        url: `${ApiPath.getSupplierDetails}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: SupplierData }) => response.data,
      providesTags: ["Suppliers"],
    }),

    supplierSearch: builder.query<SupplierListResponse, string>({
      query: (search) => ({
        url: `${ApiPath.supplierSearch}${search ? `?name=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        if (!response || !response.data || response.data.length === 0) {
          return { data: [] }; // If no data is returned, return an empty array
        }
        return response;
      },
    }),

    addAndUpdateSupplier: builder.mutation<SupplierData, UpdateSupplierRequest>(
      {
        query: (payload) => {
          if ("_id" in payload) {
            return {
              url: `${ApiPath.updateSupplier}/${payload._id}`,
              method: "PUT",
              body: payload.data,
            };
          }
          return {
            url: ApiPath.createSupplier,
            method: "POST",
            body: payload.data,
          };
        },
        invalidatesTags: ["Suppliers"],
      }
    ),

    UpdateSupplierApiKey: builder.mutation<any, any>({
      query: (payload) => {
        if ("_id" in payload) {
          return {
            url: `${ApiPath.updateSupplierApiKey}/${payload._id}`,
            method: "PUT",
            body: payload.data,
          };
        }
      },
      invalidatesTags: ["Suppliers"],
    }),

    deleteSupplier: builder.mutation<any, string>({
      query: (id) => ({
        url: `${ApiPath.deleteSupplier}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Suppliers"],
    }),

    getMemberships: builder.query<MembershipData, void>({
      query: () => ({
        url: `${ApiPath.getMemberships}`,
        method: "GET",
      }),
      providesTags: ["Memberships"],
    }),

    addMembership: builder.mutation<MembershipData, Partial<MembershipData>>({
      query: (data) => ({
        url: `${ApiPath.createMembership}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Memberships"],
    }),

    updateMembership: builder.mutation<
      MembershipData,
      { _id: string; data: Partial<MembershipData> }
    >({
      query: ({ _id, data }) => ({
        url: `${ApiPath.updateMembership}/${_id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Memberships"],
    }),

    updateMembershipOrder: builder.mutation<
      MembershipData,
      { _id: string; data: any[] }
    >({
      query: ({ _id, data }) => ({
        url: `${ApiPath.updateMembership}/${_id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Memberships"],
    }),

    deleteMembership: builder.mutation<void, string>({
      query: (_id) => ({
        url: `${ApiPath.deleteMembership}/${_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Memberships"],
    }),

    getSupplierAndProductMapping: builder.query<
      ProductAndSupplierMapModel,
      { _id: string; type: "static" | "dynamic" }
    >({
      query: ({ _id, type }) => ({
        url: `${ApiPath.getSupplierAndProductMapping}/${_id}/${type}`,
        method: "GET",
      }),
      transformResponse: (response: any): ProductAndSupplierMapModel => {
        return Convert.toProductAndSupplierMapModel(JSON.stringify(response));
      },
      providesTags: ["ProductSuppliersMapping"],
    }),

    addAndUpdateProductAndSupplierMap: builder.mutation<any, any>({
      query: (payload) => {
        return {
          url: `${ApiPath.createOrUpdateProductSupplierDenominationId}`,
          method: "POST",
          body: payload.data,
        };
      },
      // Only refetch when the save actually succeeded. Invalidating on failure
      // refetched the server's copy over the form, wiping the row the admin had
      // just filled in — so a validation error cost them their work instead of
      // telling them what to change.
      invalidatesTags: (_result, error) =>
        error ? [] : ["ProductSuppliersMapping"],
    }),

    deleteSupplierAndProductMapping: builder.mutation<
      void,
      { supplierId: string; productId: string }
    >({
      query: (parms) => ({
        url: `${ApiPath.deleteSupplierAndProductMapping}/${parms.productId}/${parms.supplierId}`,
        method: "DELETE",
      }),
      // invalidatesTags: ["ProductSuppliersMapping"],
    }),

    deleteProductAndSupplierMapIndividually: builder.mutation<
      void,
      { _id: string }
    >({
      query: (parms) => ({
        url: `${ApiPath.deleteProductAndSupplierMapIndividually}/${parms._id}`,
        method: "DELETE",
      }),
    }),

    getDiscountsList: builder.query<any, string>({
      query: (queryParam) => ({
        url: `${ApiPath.getDiscountsFileList}${queryParam}`,
        method: "GET",
      }),
    }),

    /**
     * Send a request to update or create a supplier top up setting.
     * @param {object} payload - The payload to send to the server.
     * @param {string} payload._id - The id of the supplier top up setting to update.
     * @param {object} payload.data - The data to send to the server.
     * @returns {object} - The response from the server.
     */
    SupplierSettingAddAndUpdate: builder.mutation<SupplierData, any>({
      query: (payload) => {
        if ("_id" in payload) {
          return {
            url: `${ApiPath.supplierTopupSettingsUpdate}/${payload._id}`,
            method: "PUT",
            body: payload.data,
          };
        }
        return {
          url: ApiPath.supplierTopupSettingsCreate,
          method: "POST",
          body: payload.data,
        };
      },
    }),

    getSupplierTopupSettings: builder.query<any, void>({
      query: () => ({
        url: ApiPath.supplierTopupSettingsGet,
        method: "POST",
        body: {
          filters: [
            { key: "supplierData.isActive", value: true },
            { key: "supplierData.isDelete", value: false },
          ],
        },
      }),
    }),

    dynamicSaveLinkProductWithSupplierProduct: builder.mutation<
      any,
      { reqBody: any }
    >({
      query: (payload) => ({
        url: ApiPath.dynamicLinkProductWithSupplierProduct,
        method: "POST",
        body: { data: payload.reqBody },
      }),
    }),

    getExistingDynamicMapped: builder.query<any, string>({
      query: (_id) => ({
        url: `${ApiPath.supplierProductPriceList}/${_id}`,
        method: "GET",
      }),
    }),

    getSupplierProductPriceList: builder.query<
      any,
      { supplierId: string; providerCode: string; subSupplierId: string }
    >({
      query: (payload) => ({
        url: ApiPath.supplierProductPriceList,
        method: "POST",
        body: {
          filters: [
            { key: "supplierId", value: payload.supplierId },
            { key: "subSupplierId", value: payload.subSupplierId },
            { key: "providerCode", value: payload.providerCode },
          ],
        },
      }),
    }),

    dynamicGetProductWithSupplierActiveList: builder.query<any, { productId: string, supplierId?: string, subSupplierId?: string; supplierProviderCode: string }>({
      query: (payload) => ({

        url: ApiPath.dynamicProductWithSupplierActiveList,
        method: 'POST',
        body: {
          "filters": [
            // { "key": "supplierId", "value": payload.supplierId },
            { "key": "subSupplierId", "value": payload.subSupplierId },
            // { "key": "productId", "value": payload.productId },
            { "key": "supplierProviderCode", "value": payload.supplierProviderCode },
            { "key": "linkStatus", "value": true }
          ]
        }
      }),
      transformResponse: (response: { data: any }) => {
        if (response?.data && Array.isArray(response.data)) {

          // Create a flattened array of all products
          const flattenedProducts: any[] = [];

          response.data.forEach((item: any) => {
            if (item.supplierProductData && item.supplierProductData.length > 0) {
              item.supplierProductData.forEach((productData: any) => {
                flattenedProducts.push({
                  ...productData.supplierproductId,
                  supplerProductDenominationsku: productData.supplerProductDenominationsku,
                  linkStatus: productData.linkStatus,
                  createdBy: productData.createdBy,
                  createdAt: productData.createdAt,
                  updatedAt: productData.updatedAt
                });
              });
            }
          });

          return flattenedProducts;
        }
        return [];
      }
    }),

 
    deleteSupplierUnlink: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ApiPath.deleteSupplierUnlink}/${id}`,
        method: "DELETE",
      }),
    }),

    allSupplierProvidersList: builder.query<
      any,
      {
        supplierId: string;
        page: number;
        pageSize: number;
        countryIso?: string;
        subSupplierId: string;
        productName?: string;
      }
    >({
      query: (payload) => ({
        url: ApiPath.allSupplierProvidersList,
        method: "POST",
        body: {
          filters: [
            { key: "supplierId", value: `${payload.supplierId}` },
            { key: "subSupplierId", value: `${payload.subSupplierId}` },
            ...(payload.countryIso
              ? [{ key: "countryIso", value: payload.countryIso }]
              : []),
            ...(payload.productName
              ? [{ key: "providerName", value: payload.productName }]
              : []),
            { key: "isActive", value: true },
            { key: "isDelete", value: false },
          ],
          sort: "desc",
          page: payload.page,
          pageSize: payload.pageSize,
        },
      }),
      providesTags: ["SUPPLIER_PROVIDER"],
    }),
    /**@SUPPLIER_SYNC_HISTORY — every catalogue sync run, scheduled and manual */
    getSupplierSyncRuns: builder.query<any, {
      page: number;
      pageSize: number;
      supplierId?: string;
      status?: string;
      trigger?: string;
      from?: string;
      to?: string;
      search?: string;
    }>({
      query: (p) => {
        const qs = new URLSearchParams();
        qs.set("page", String(p.page));
        qs.set("pageSize", String(p.pageSize));
        (["supplierId", "status", "trigger", "from", "to", "search"] as const)
          .forEach((k) => { if (p[k]) qs.set(k, String(p[k])); });
        return { url: `${ApiPath.supplierSyncRuns}?${qs.toString()}`, method: "GET" };
      },
    }),

    /**
     * Every credential check made against a supplier's API, accepted or
     * refused. Nothing was recorded before, so a refusal left no evidence of
     * what the supplier said or when we asked.
     */
    getSupplierValidationLogs: builder.query<any, {
      page: number;
      pageSize: number;
      /** Omitted on the all-suppliers screen. See the note below. */
      supplierId?: string;
      outcome?: string;
      search?: string;
    }>({
      query: (p) => {
        const qs = new URLSearchParams();
        qs.set("page", String(p.page));
        qs.set("pageSize", String(p.pageSize));
        /*
         * Only when there is one. URLSearchParams stringifies whatever it is
         * given, so setting this unconditionally sent the literal
         * "supplierId=undefined" from the all-suppliers screen -- the server
         * dutifully filtered on a supplier named "undefined" and returned
         * nothing, which looked exactly like the log not recording anything.
         */
        if (p.supplierId) qs.set("supplierId", p.supplierId);
        if (p.outcome && p.outcome !== "all") qs.set("outcome", p.outcome);
        if (p.search) qs.set("search", p.search);
        return { url: `${ApiPath.supplierValidationLogs}?${qs.toString()}`, method: "GET" };
      },
    }),

    getSupplierSyncSummary: builder.query<any, void>({
      query: () => ({ url: ApiPath.supplierSyncSummary, method: "GET" }),
    }),

    /**@VALIDATE_SUB_SUPPLIER */
    validateSubSupplier: builder.mutation<any, any>({
      query: (reqBody) => ({
        url: ApiPath.validateSubSupplier,
        method: "POST",
        body: reqBody,
      }),
    }),

    /**@ADD_AND_UPDATE_SUPPLIER_PRODUCT_FROM_ADMIN_PANEL_FOR_CC_CARD */
    addAndUpdateSupplierProductFromAdminPanelForCcCard: builder.mutation<
      any,
      any
    >({
      query: (reqBody) => {
        const { _id, ...bodyWithoutId } = reqBody; // Destructure to remove _id
        return {
          url: _id
            ? `${ApiPath.updateSupplierProviderManualy}/${_id}`
            : ApiPath.createSupplierProviderManualy,
          method: _id ? "PUT" : "POST",
          body: _id ? bodyWithoutId : reqBody,
        };
      },
    }),

    // Removes a manually-added provider and its denominations. The API rejects
    // anything that is not source === "web", so synced catalogues are safe.
    deleteManualSupplierProvider: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${ApiPath.deleteManualSupplierProvider}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SUPPLIER_PROVIDER"],
    }),

    saveSupplierProviderDenominationsManually: builder.mutation<any, any>({
      query: (reqBody) => ({
        url: ApiPath.saveSupplierProviderDenominationsManually,
        method: "POST",
        body: reqBody,
      }),
      invalidatesTags: ["SUPPLIER_PROVIDER"],
    }),

    getInventoryTempRecordsList: builder.query<InventoryLogsResponse, any>({
      query: (reqBody) => ({
        url: ApiPath.inventoryTempRecordsList,
        method: "POST",
        body: reqBody,
      }),
      transformResponse: (response: any): InventoryLogsResponse => {
        const {
          status,
          totalRecords,
          totalPages,
          currentPage,
          pageSize,
          data,
        } = response;

        const { validRecords, existingRecords, invalidRecords, ...baseData } =
          data ?? {};

        let allRecords: RecordsList = { pins: [] };
        if (Array.isArray(validRecords?.pins) && validRecords?.pins?.length) {
          allRecords = validRecords;
        } else if (
          Array.isArray(existingRecords?.pins) &&
          existingRecords?.pins?.length
        ) {
          allRecords = existingRecords;
        } else if (Array.isArray(invalidRecords) && invalidRecords?.length) {
          allRecords.pins = invalidRecords;
        }

        return {
          status,
          totalRecords,
          totalPages,
          currentPage,
          pageSize,
          data: {
            _id: baseData._id,
            fileName: baseData.fileName,
            filePath: baseData.filePath,
            createdAt: baseData.createdAt || null,
            allRecords,
          },
        };
      },
    }),

    getSupplierApiKeyList: builder.query<any, any>({
      query: (params) => ({
        url: `${ApiPath.getsupplierapiinfo}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "GET",
      }),
      providesTags: ["Suppliers"],
    }),

    saveSubSuppliersManually: builder.mutation<any, any>({
      query: (reqBody) => ({
        url: ApiPath.createsupplieraccount,
        method: "POST",
        body: reqBody,
      }),
      invalidatesTags: ["SUPPLIER_PROVIDER"],
    }),

    updateSubSuppliersManually: builder.mutation<
      any,
      { _id: string; data: any }
    >({
      query: ({ _id, data }) => ({
        url: `${ApiPath.updateSupplierAccount}/${_id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["SUPPLIER_PROVIDER"],
    }),

    getSubSuppliersForAll: builder.query<any, any>({
      query: (supplierId) => ({
        url: `${ApiPath.getSubSuppliersOfAll}`,
        method: "GET",
      }),
      providesTags: ["Suppliers"],
    }),

    getSupplierCredentials: builder.query<any, any>({
      query: (supplierId) => ({
        url: `${ApiPath.getSupplierCredentials}?supplierId=${supplierId}`,
        method: "GET",
      }),
      providesTags: ["Suppliers"],
    }),

    getSubSuppliersBySupplierId: builder.query<any, any>({
      query: (supplierId) => ({
        url: `${ApiPath.getSubSuppliersBySupplierId}/${supplierId}`,
        method: "GET",
      }),
      providesTags: ["Suppliers"],
    }),

    getLatestProductsofSuppliers: builder.query<any,any>({
        query: (reqBody) => ({
        url: `${ApiPath.getLatestProductsFromCron}`,
        method: "POST",
        body: reqBody
      }),
      providesTags: ["SUPPLIER_PROVIDER"],
    })
  }),
});

export const {
  useLazyGetSupplierSyncRunsQuery,
  useLazyGetSupplierValidationLogsQuery,
  useGetSupplierSyncSummaryQuery,
  useGetSupplierListQuery,
  useLazyGetSupplierListQuery,
  useAddAndUpdateSupplierMutation,
  useValidateSubSupplierMutation,
  useDeleteSupplierMutation,
  useLazySupplierSearchQuery,
  useGetSupplierDetailsQuery,
  useLazyGetSupplierDetailsQuery,

  useGetExistingDynamicMappedQuery,
  useLazyGetSupplierProductPriceListQuery,
  useDynamicSaveLinkProductWithSupplierProductMutation,
  useLazyDynamicGetProductWithSupplierActiveListQuery,

  useGetMembershipsQuery,
  useAddMembershipMutation,
  useUpdateMembershipMutation,
  useDeleteMembershipMutation,
  useUpdateMembershipOrderMutation,

  useAddAndUpdateProductAndSupplierMapMutation,
  useGetSupplierAndProductMappingQuery,
  useLazyGetSupplierAndProductMappingQuery,
  useDeleteSupplierAndProductMappingMutation,

  useSupplierSettingAddAndUpdateMutation,
  useLazyGetSupplierTopupSettingsQuery,
  useDeleteSupplierUnlinkMutation,
  useLazyAllSupplierProvidersListQuery,

  useDeleteProductAndSupplierMapIndividuallyMutation,

  useAddAndUpdateSupplierProductFromAdminPanelForCcCardMutation,
  useSaveSupplierProviderDenominationsManuallyMutation,
  useDeleteManualSupplierProviderMutation,
  useLazyGetInventoryTempRecordsListQuery,

  useGetSupplierApiKeyListQuery,
  useUpdateSupplierApiKeyMutation,

  useLazyGetAllSupplierListQuery,
  useLazyGetDiscountsListQuery,
  useSaveSubSuppliersManuallyMutation,
  useLazyGetSubSuppliersForAllQuery,
  useLazyGetSupplierCredentialsQuery,
  useLazyGetSubSuppliersBySupplierIdQuery,
  useUpdateSubSuppliersManuallyMutation,
  useLazyGetLatestProductsofSuppliersQuery
} = SupplierService;
